import React, { useEffect, useMemo, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import ModeEditOutlinedIcon from "@mui/icons-material/ModeEditOutlined";
import BreadcrumbComponent from "../../common/components/breadcrumb-component/breadcrumb-component";
import ChangePassword from "../change-password/change-password";
import "./personal-settings.scss";
import phoneIcon from "../../assets/icons/phone.svg";
import emailIcon from "../../assets/icons/mail.svg";
import avatorIcon from "../../assets/icons/avatar.svg";
import CountryCodeWithPhoneNumber from "../../common/components/phone-number-input-component/country-code-phone-numner-input";
import {
  getUserDetails,
  updateProfileUsers,
  updateUserProfile,
} from "../../api/settings";
import { AlertComponent } from "../../common/components";
import {
  getCurrentTenantId,
  isOnlyCountryCode,
  isValidPhoneNumber,
} from "../../utils/utils";
import { getCurrentUser } from "../../common/routines/user-operations";
import { useSetLoading } from "../../common/hooks/appearance";
import phoneValidation from "../../common/components/phone-number-input-component/phone-validation";
import store from "../../store";
import { emailValidationRegex } from "../../constants/regexStrings";

const validationSchema = Yup.object({
  firstName: Yup.string(),
  lastName: Yup.string(),
  email: Yup.string().matches(emailValidationRegex, "Invalid email").required("Email is required"),
  phoneNumber: Yup.string()
    .test(
      "valid-phone-check",
      "Please enter a valid phone number",
      function (value) {
        if (!value) return false;

        if (isOnlyCountryCode(value)) {
          return this.createError({
            message: "Phone number is required",
          });
        }

        if (!phoneValidation(value)) {
          return this.createError({
            message: "Please enter a valid phone number",
          });
        }

        return true;
      }
    )
    .required("Phone number is required"),
});

const PersonalSettings: React.FC = () => {
  const crumbMapping = [{ label: "Settings", link: "/settings" }];
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [profile, setProfile] = useState<any>(null);
  const [alert, setAlert] = useState<{
    isOpen?: boolean;
    message?: string;
    type?: string;
  }>({});
  const [loading, setLoading] = useState<boolean>(false);

  const tenantId = getCurrentTenantId();
  const currentUser = getCurrentUser();
  const userId = currentUser?.id;

  const fetchUserProfile = async () => {
    setLoading(true);
    try {
      const response = await getUserDetails(userId);
      setProfile(response.data?.data?.data?.data);
    } catch (error: any) {
      setAlert({
        isOpen: true,
        message:
          error?.response?.data?.message || "Failed to fetch user profile",
        type: "error",
      });
      console.error("Error fetching user profile:", error);
      return null;
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchUserProfile();
  }, [tenantId]);

  const userInfo = profile?.find((user: any) => userId === user?.id);

  const handleOpen = () => setIsModalOpen(true);
  const handleClose = () => setIsModalOpen(false);

  const initialValues = {
    firstName: userInfo?.first_name || "",
    lastName: userInfo?.last_name || "",
    email: userInfo?.email || "",
    phoneNumber: userInfo?.phone_number || "",
  };
  const allowedImageTypes = [
    "image/jpeg",
    "image/png",
    "image/jpg",
    "image/webp",
    "image/svg+xml",
  ];

  const max_profile_image_size_in_MB = 110;

  const handleProfileUpdate = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    if (!allowedImageTypes.includes(file.type)) {
      setAlert({
        isOpen: true,
        message: "Only JPG, JPEG, PNG, SVG or WEBP image formats are allowed.",
        type: "error",
      });
      return;
    }
    if (file.size > max_profile_image_size_in_MB * 1024 * 1024) {
      setAlert({
        isOpen: true,
        message: `Image size should not exceed ${max_profile_image_size_in_MB}MB.`,
        type: "error",
      });
      return;
    }
    const formData = new FormData();
    formData.append("file", file);
    setLoading(true);
    const response = await updateUserProfile(formData);
    setLoading(false);
    if (response?.data?.success) {
      setAlert({
        isOpen: true,
        message: "Profile picture updated successfully",
        type: "success",
      });
      setProfile((prev: any[]) => {
        return prev.map((user) => {
          if (user.id === userId) {
            return {
              ...user,
              profile_url: response?.data?.data?.profile_url,
            };
          }
          return user;
        });
      });
      const loginDataStr = localStorage.getItem("loginData");

      if (loginDataStr) {
        const loginData = JSON.parse(loginDataStr);

        loginData.userDetails.profile_url = response?.data?.data?.profile_url;
        localStorage.setItem("loginData", JSON.stringify(loginData));

        store.dispatch({
          type: "SESSION:SET_IS_LOGGED_IN",
          payload: {
            value: true,
            tenantDetails: loginData.tenantDetails,
            userDetails: loginData.userDetails,
          },
        });
      }
    } else {
      setAlert({
        isOpen: true,
        message: response?.data?.message || "Failed to update profile picture",
        type: "error",
      });
    }
  };

  const handleSubmit = async (
    values: typeof initialValues,
    { resetForm }: { resetForm: () => void }
  ) => {
    const userCurrentdetails = profile?.find(
      (user: any) => userId === user?.id
    );

    if (!userCurrentdetails) {
      console.error("User not found.");
      return;
    }

    const userPayload = {
      users: [
        {
          id: userCurrentdetails.id,
          isUpdate: true,
          isPrimaryUser: userCurrentdetails?.is_primary_user,
          roleId: userCurrentdetails?.role?.id,
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          phoneNumber: values.phoneNumber,
        },
      ],
    };

    try {
      const response = await updateProfileUsers(Number(tenantId), userPayload);
      if (response?.data?.success) {
        await fetchUserProfile();
        setIsEditing(false);
        setAlert({
          isOpen: true,
          message:
            response?.data?.message || "Personal details updated successfully",
          type: "success",
        });
      } else {
        console.error("Update failed");
      }
    } catch (error: any) {
      console.error("Failed to update profile:", error);
      setAlert({
        isOpen: true,
        message: error?.response?.data?.message || "Failed to update profile",
        type: "error",
      });
    }
    resetForm();
  };

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  return (
    <div className="personal-settings-wrapper personal-settings-wrapper-mobile-view">
      <BreadcrumbComponent breadcrumbLink={crumbMapping} />
      {userInfo && (
        <div className="profile-card-wrapper">
          <div className="user-profile-icon-wrapper">
            <img
              src={userInfo?.profile_url || avatorIcon}
              className="user-profile-icon"
              alt="profile"
              width={110}
            />
            <div
              className="edit-overlay"
              onClick={() => {
                document.getElementById("upload-input")?.click();
              }}
            >
              <ModeEditOutlinedIcon className="edit-icon" />
            </div>
            <input
              type="file"
              id="upload-input"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleProfileUpdate}
            />
          </div>
          <div className="profile-card">
            <h2>{userInfo.first_name + " " + userInfo.last_name}</h2>
            <div className="contact-info">
              <img src={phoneIcon} alt="phone" />
              <p>
                {isValidPhoneNumber(userInfo?.phone_number)
                  ? userInfo?.phone_number
                  : "N/A"}
              </p>
              <div className="divider"></div>
              <img src={emailIcon} alt="email" />
              <p>{userInfo.email || "N/A"}</p>
            </div>
            <div className="profile-status">
              <p>{userInfo?.status}</p>
            </div>
          </div>
        </div>
      )}

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          resetForm,
          values,
          errors,
          touched,
          setFieldTouched,
          setFieldValue,
          submitCount,
        }) => (
          <Form className="details-card-wrapper">
            <div className="edit-details">
              {!isEditing && (
                <button
                  className="edit-details-btn"
                  type="button"
                  onClick={() => {
                    resetForm();
                    setIsEditing(true);
                  }}
                >
                  Edit Details
                </button>
              )}
            </div>

            <div className="details">
              <div className="name-details">
                <div className="name">
                  <p>First Name</p>
                  {isEditing ? (
                    <>
                      <Field
                        name="firstName"
                        placeholder="Enter First Name"
                        disabled
                      />
                      <ErrorMessage
                        name="firstName"
                        component="div"
                        className="error"
                      />
                    </>
                  ) : (
                    <p>{values.firstName}</p>
                  )}
                </div>
                <div className="name">
                  <p>Last Name</p>
                  {isEditing ? (
                    <>
                      <Field
                        name="lastName"
                        placeholder="Enter Last Name"
                        disabled
                      />
                      <ErrorMessage
                        name="lastName"
                        component="div"
                        className="error"
                      />
                    </>
                  ) : (
                    <p>{values.lastName}</p>
                  )}
                </div>
              </div>

              <div className="name-details">
                <div className="name">
                  <p>Email Address</p>
                  {isEditing ? (
                    <div>
                      <Field
                        name="email"
                        type="email"
                        placeholder="Enter Email"
                      />
                      {submitCount > 0 && errors.email && (
                        <div className="error-message">
                          {typeof errors.email === "string" && errors.email}
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="profile-email">{values.email || "N/A"}</p>
                  )}
                </div>
                <div className="name">
                  <p>Phone</p>
                  {isEditing ? (
                    <>
                      <div>
                        <CountryCodeWithPhoneNumber
                          fieldName="phoneNumber"
                          placeholder="Enter phone number"
                          onChange={(value: string, country: any) => {
                            const countryCode = country?.dialCode;

                            if (!countryCode) {
                              console.warn(
                                "Missing dialCode in country object"
                              );
                              return;
                            }

                            const numberOnly = value.slice(countryCode.length + 1);
                            const formatted = `+${countryCode} ${numberOnly}`;

                            setFieldValue("phoneNumber", formatted);
                          }}
                          onBlur={() => {
                            setFieldTouched("phoneNumber", true);
                          }}
                          value={values?.phoneNumber || ""}
                          defaultCountry="us"
                        />
                        {submitCount > 0 &&
                          typeof errors.phoneNumber === "string" && (
                            <div className="error-message">
                              {errors.phoneNumber}
                            </div>
                          )}
                      </div>
                    </>
                  ) : (
                    <p>
                      {isValidPhoneNumber(values.phoneNumber)
                        ? values.phoneNumber
                        : "N/A"}
                    </p>
                  )}
                </div>
              </div>
            </div>
            <div className="details">
              <div className="name-details">
                <div className="name">
                  <p>User Name</p>

                  <p>{userInfo?.username || "-"}</p>
                </div>
              </div>
              <div className="name-details">
                <div className="name">
                  <p>Role</p>

                  <p>{userInfo?.role?.role || "-"}</p>
                </div>
              </div>
            </div>
            {isEditing ? (
              <div className="name-details-buttons">
                <button
                  className="cancelButton"
                  type="button"
                  onClick={() => {
                    resetForm();
                    setIsEditing(false);
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="submitButton">
                  Save
                </button>
              </div>
            ) : (
              <></>
            )}
          </Form>
        )}
      </Formik>

      <div className="password-card-wrapper">
        <div>
          <h2>Password</h2>
          <p>Please ensure your password is strong and secure.</p>
        </div>
        <button className="change-password-btn" onClick={handleOpen}>
          Change Password
        </button>
      </div>
      <ChangePassword isOpen={isModalOpen} onClose={handleClose} />
      {alert?.isOpen && (
        <AlertComponent
          message={alert?.message || ""}
          type={
            ["info", "success", "error", "warning"].includes(alert?.type || "")
              ? (alert?.type as "info" | "success" | "error" | "warning")
              : "info"
          }
          open={alert?.isOpen}
          onClose={() => setAlert({})}
        />
      )}
    </div>
  );
};

export default PersonalSettings;
